import React, { useState, useCallback } from 'react';
import { 
  Upload, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle,
  Eye,
  ChevronRight,
  ChevronUp,
  FileText,
  Database,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { API_ORIGIN } from '../config/api';
import { useLanguage } from '../i18n/LanguageContext';

interface FilePreview {
  fileName: string;
  fileSize: number;
  preview: string;
  stats: {
    characters: number;
    words: number;
    lines: number;
    estimatedChunks: number;
  };
  metadata: any;
}

interface FileUploaderWithPreviewProps {
  onUploadComplete?: (results: any) => void;
  multiple?: boolean;
}

export const FileUploaderWithPreview: React.FC<FileUploaderWithPreviewProps> = ({ 
  onUploadComplete, 
  multiple = false 
}) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [selectedFile, setSelectedFile] = useState<number | null>(null);
  const [uploadResults, setUploadResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'complete'>('upload');

  const API_BASE_URL = API_ORIGIN;

  // 处理文件拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // 处理文件放置
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  // 处理文件选择
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  }, []);

  // 处理文件列表
  const handleFiles = (newFiles: File[]) => {
    const supportedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown'
    ];
    
    const validFiles = newFiles.filter(file => 
      supportedTypes.includes(file.type) || 
      file.name.endsWith('.txt') || 
      file.name.endsWith('.md')
    );
    
    if (validFiles.length === 0) {
      setError(t('不支持的文件类型。请上传 PDF、Word 或文本文件。', 'Unsupported file type. Please upload a PDF, Word, or text file.'));
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
    setStep('upload');
  };

  // 移除文件
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 预览文件
  const previewFiles = async () => {
    if (files.length === 0) return;
    
    setPreviewing(true);
    setError(null);
    setPreviews([]);
    
    try {
      const previewResults = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_BASE_URL}/api/upload/preview`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        previewResults.push(response.data);
      }
      
      setPreviews(previewResults);
      setStep('preview');
      setSelectedFile(0); // 选中第一个文件
      
    } catch (err: any) {
      console.error('预览错误:', err);
      setError(err.response?.data?.error || err.message || t('预览失败', 'Preview failed'));
    } finally {
      setPreviewing(false);
    }
  };

  // 确认上传到知识库
  const confirmUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setError(null);
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append(multiple ? 'files' : 'file', file);
    });
    
    try {
      const endpoint = multiple ? `${API_BASE_URL}/api/upload/files` : `${API_BASE_URL}/api/upload/file`;
      
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(response.data.data || [response.data]);
      setStep('complete');
      setFiles([]);
      setPreviews([]);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
      
    } catch (err: any) {
      console.error('上传错误:', err);
      setError(err.response?.data?.error || err.message || t('上传失败', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  // 取消并重新选择文件
  const cancelAndRestart = () => {
    setFiles([]);
    setPreviews([]);
    setStep('upload');
    setSelectedFile(null);
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('上传文档到知识库', 'Upload Documents to Knowledge Base')}
      </h3>
      
      {/* 步骤指示器 */}
      <div className="flex items-center justify-between mb-6">
        <div className={`flex items-center ${step === 'upload' ? 'text-indigo-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step === 'upload' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
          }`}>1</div>
          <span className="ml-2 text-sm">{t('选择文件', 'Select files')}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <div className={`flex items-center ${step === 'preview' ? 'text-indigo-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step === 'preview' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
          }`}>2</div>
          <span className="ml-2 text-sm">{t('预览内容', 'Preview')}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <div className={`flex items-center ${step === 'complete' ? 'text-green-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step === 'complete' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
          }`}>3</div>
          <span className="ml-2 text-sm">{t('完成', 'Done')}</span>
        </div>
      </div>
      
      {/* 步骤1: 选择文件 */}
      {step === 'upload' && (
        <>
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileSelect}
              multiple={multiple}
              accept=".pdf,.doc,.docx,.txt,.md"
            />
            
            <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {t('拖拽文件到这里，或', 'Drag files here, or')}
              <label
                htmlFor="file-upload"
                className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer ml-1"
              >
                {t('浏览文件', 'browse files')}
              </label>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('支持 PDF、Word、TXT、Markdown（最大10MB）', 'Supports PDF, Word, TXT, and Markdown files up to 10MB')}
            </p>
          </div>
          
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t(`已选择 ${files.length} 个文件`, `${files.length} file(s) selected`)}
              </h4>
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <File className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
              
              <div className="flex gap-2 mt-3">
                <button
                  onClick={previewFiles}
                  disabled={previewing}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {previewing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {t('解析中...', 'Parsing...')}
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      {t('预览文件内容', 'Preview file content')}
                    </>
                  )}
                </button>
                <button
                  onClick={cancelAndRestart}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('清空', 'Clear')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* 步骤2: 预览内容 */}
      {step === 'preview' && previews.length > 0 && (
        <div className="space-y-4">
          {/* 文件切换标签 */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {previews.map((preview, index) => (
              <button
                key={index}
                onClick={() => setSelectedFile(index)}
                className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap flex items-center gap-2 ${
                  selectedFile === index
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                {preview.fileName}
              </button>
            ))}
          </div>
          
          {selectedFile !== null && previews[selectedFile] && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* 文件信息 */}
              <div className="bg-gray-50 dark:bg-gray-700 p-3 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {previews[selectedFile].fileName}
                  </h4>
                  <span className="text-xs text-gray-500">
                    {formatFileSize(previews[selectedFile].fileSize)}
                  </span>
                </div>
                <div className="flex gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                  <span>{t('字数', 'Words')}: {previews[selectedFile].stats.words}</span>
                  <span>{t('行数', 'Lines')}: {previews[selectedFile].stats.lines}</span>
                  <span>{t('预计分块', 'Estimated chunks')}: {previews[selectedFile].stats.estimatedChunks}</span>
                </div>
              </div>
              
              {/* 内容预览 */}
              <div className="p-4 max-h-96 overflow-y-auto bg-white dark:bg-gray-800">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                  {previews[selectedFile].preview}
                </pre>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={confirmUpload}
              disabled={uploading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('添加到知识库...', 'Adding to knowledge base...')}
                </>
              ) : (
                <>
                  <Database className="w-4 h-4" />
                  {t('确认添加到知识库', 'Add to knowledge base')}
                </>
              )}
            </button>
            <button
              onClick={cancelAndRestart}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('重新选择', 'Choose again')}
            </button>
          </div>
        </div>
      )}
      
      {/* 步骤3: 完成 */}
      {step === 'complete' && uploadResults.length > 0 && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                {t('文件已成功添加到知识库', 'Files were added to the knowledge base')}
              </span>
            </div>
            
            <ul className="space-y-2">
              {uploadResults.map((result, index) => (
                <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">{result.fileName}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    {t(`${result.chunks || 1} 个文档块, ${result.wordCount || 0} 字`, `${result.chunks || 1} chunk(s), ${result.wordCount || 0} words`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={cancelAndRestart}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t('上传更多文件', 'Upload more files')}
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('返回聊天', 'Back to chat')}
            </button>
          </div>
        </div>
      )}
      
      {/* 错误提示 */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        </div>
      )}
    </div>
  );
};
